import React, { Component, ChangeEvent, KeyboardEvent } from 'react';
import { connect } from 'react-redux';
import {
  Alert,
  Input,
  InputGroup,
  InputGroupText,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Progress,
  UncontrolledTooltip
} from 'reactstrap';
import * as actions from '../../actions';
import history from '../../helpers/history';
import DateTimePicker from 'react-widgets/lib/DateTimePicker';
import { UserBrandSelection } from '../Brand/Brands';
import classnames from 'classnames';
import GalleryPresetSelect from './GalleryPresetSelect';
import { fetchGalleriesTags } from '../../modules/actions';
import { languageLocalizationMap } from '../../data/languageLocalizationMap';
import 'react-widgets/dist/css/react-widgets.css';
import moment, { Moment } from 'moment';
import { getTranslationKey } from '../../modules/utils/helpers';
import {
  getReplacementTemplateId,
  getTemplateDescription,
  getTemplateTitle
} from '../../modules/selectors/templates';
import type { Notify } from '../../typings/redux';

interface FaceRecognition {
  enabled: boolean;
}

interface GalleryPreset {
  _id: string;
  name: string;
  faceRecognition?: FaceRecognition;
  _userbrand?: string;
  _scrappbookGalleryTemplate?: string;
  [key: string]: any;
}

interface ScrappbookTemplate {
  _id: string;
  name: string;
  displayName: string;
  newDisplayName?: string;
  previewUrl: string;
  assetUrl: string;
  userIsAllowedToUseTemplate: boolean;
  inDevelopment?: boolean;
  isPurchable?: boolean;
  presale?: boolean;
}

interface CreateCollectionState {
  name: string;
  title: string;
  type: string;
  step: number;
  selectedTemplateId: string;
  showPreviewVideo: boolean;
  selectedTemplate: Partial<ScrappbookTemplate>;
  limitIsExceeded: boolean;
  showEmailConfirmation: boolean;
  emailConfirmationBody: string;
  hideEmailConfirmationButton: boolean;
  isSmallScreen: boolean;
  faceRecognition: FaceRecognition;
  eventDate?: Date | Moment;
  userBrand?: string;
  galleryPreset?: GalleryPreset;
  loading?: boolean;
}

interface AvailableRules {
  collectionsLeft: number;
}

interface Subscription {
  plan?: string;
  purchased_templates: string[];
}

interface RegistrationProcess {
  confirmed: boolean;
}

interface CreateCollectionProps {
  userType: string;
  scrappbookTemplates: ScrappbookTemplate[];
  collectionsIds: string[];
  collectionModal: boolean;
  availableRules: AvailableRules;
  defaultUserBrand: any;
  subscription: Subscription;
  registrationProcess: RegistrationProcess;
  notify: Notify;
  renderWithOutModal?: boolean;
  onboardingProcess?: boolean;
  setCollectionsModalOpenState: (params: { key: string; state: boolean }) => void;
  getCollectionsIds: (params: { withInitialCollectionsCount: boolean }) => void;
  fetchGalleriesTags: () => void;
  isNewDesign: boolean;
  createCollection: (params: any) => Promise<{ collection_id?: string; err?: string }>;
  setUpgradeAccountOpenState: (params: any) => void;
  trackEventAction: (params: any) => void;
  sendConfirmationEmail: () => Promise<any>;
  setModalOpenState: (params: any) => void;
  logUserActivity: (params: any) => void;
}

class CreateCollection extends Component<CreateCollectionProps, CreateCollectionState> {
  private titleRef: React.RefObject<HTMLInputElement>;

  constructor(props: CreateCollectionProps) {
    super(props);

    this.titleRef = React.createRef();

    this.state = {
      name: '',
      title: '',
      type: props.userType === 'SB' ? 'ScrAppBook2' : 'ScrAppBook',
      step: 1,
      selectedTemplateId: 'ScrAppBook2_Breeze',
      showPreviewVideo: true,
      selectedTemplate: {},
      limitIsExceeded: true,
      showEmailConfirmation: false,
      emailConfirmationBody: getTranslationKey('showEmailConfirmation.message'),
      hideEmailConfirmationButton: false,
      isSmallScreen: window.innerWidth < 576,
      faceRecognition: {
        enabled: false
      }
    };

    this.toggle = this.toggle.bind(this);
    this.emailConfirmationToggle = this.emailConfirmationToggle.bind(this);
    this.createCollection = this.createCollection.bind(this);
    this.onChangeText = this.onChangeText.bind(this);
    this.handleCreateKeyPress = this.handleCreateKeyPress.bind(this);
    this.setStep = this.setStep.bind(this);
    this.sendConfirmationEmail = this.sendConfirmationEmail.bind(this);

    moment.locale(languageLocalizationMap[window.LANGUAGE_SW]);
  }

  componentDidMount() {
    if (this.props.onboardingProcess) {
      window.scroll(0, 0);
    }
  }

  componentDidUpdate(prevProps: CreateCollectionProps, prevState: CreateCollectionState) {
    const { scrappbookTemplates, collectionsIds, registrationProcess, userType } = this.props;
    const { selectedTemplate } = this.state;

    if (
      (prevProps.scrappbookTemplates.length === 0 && scrappbookTemplates.length > 0) ||
      ((!selectedTemplate || !selectedTemplate._id) && scrappbookTemplates.length > 0)
    ) {
      const _selectedTemplate = scrappbookTemplates.find(
        (t) => t._id === this.state.selectedTemplateId
      );

      this.setState({ selectedTemplate: _selectedTemplate || {} });
    }

    if (
      selectedTemplate &&
      prevState.selectedTemplate &&
      selectedTemplate._id !== prevState.selectedTemplate._id
    ) {
      this.setState(
        {
          showPreviewVideo: false
        },
        () => window.setTimeout(() => this.setState({ showPreviewVideo: true }), 500)
      );
    }

    if (
      (prevProps.collectionsIds.length === 0 && collectionsIds.length > 0) ||
      (prevProps.collectionsIds.length > 0 && collectionsIds.length === 0)
    ) {
      this.setState({
        showEmailConfirmation:
          collectionsIds.length > 0 && !registrationProcess.confirmed && userType === 'SB'
      });
    }
  }

  handleCreateKeyPress(target: KeyboardEvent<HTMLDivElement>) {
    if (target.charCode === 13) {
      if (this.state.step === 1 && this.props.userType === 'SB') {
        this.setStep();
      } else {
        this.createCollection();
      }
    }
  }

  onChangeText(value: string, key: keyof CreateCollectionState) {
    this.setState({ [key]: value } as unknown as Pick<
      CreateCollectionState,
      keyof CreateCollectionState
    >);
  }

  setStep() {
    if (!this.state.title) {
      return this.props.notify({
        title: getTranslationKey('missingInput'),
        message: getTranslationKey('collection.titleRequired'),
        status: 'warning',
        position: 'top-center',
        dismissible: true,
        dismissAfter: 5000
      });
    }

    this.setState((prevState) => ({ step: prevState.step === 1 ? 2 : 1 }));
  }

  async createCollection() {
    const { setCollectionsModalOpenState, getCollectionsIds, fetchGalleriesTags } = this.props;
    const { name, title, eventDate, userBrand, selectedTemplate, galleryPreset, faceRecognition } =
      this.state;
    this.setState({ loading: true });
    const { collection_id, err } = await this.props.createCollection({
      name,
      title: title || getTranslationKey('standardTitle'),
      eventDate: eventDate ? moment(eventDate).startOf('day').toISOString() : eventDate,
      templateId: selectedTemplate._id || 'ScrAppBook2_SimpleFiligree',
      userBrand: userBrand || this.props.defaultUserBrand._id || this.props.defaultUserBrand,
      galleryPresetId: galleryPreset && galleryPreset._id,
      faceRecognition
    });

    if (err === 'generalError') {
      this.props.notify({
        title: getTranslationKey('error'),
        message: getTranslationKey('generalError'),
        status: 'error',
        position: 'top-center',
        dismissible: true,
        dismissAfter: 5000
      });
    } else if (err === 'templatePurchase') {
      this.props.notify({
        title: getTranslationKey('error'),
        message: getTranslationKey('sb_templates.templateRequired'),
        status: 'error',
        position: 'top-center',
        dismissible: true,
        dismissAfter: 10000
      });
    } else if (err === 'networkError') {
      this.props.notify({
        title: getTranslationKey('error'),
        message: getTranslationKey('offlineMessage'),
        status: 'error',
        position: 'top-center',
        dismissible: true,
        dismissAfter: 5000
      });
    } else if (collection_id) {
      this.setState({ name: '', title: '', step: 1, loading: false, eventDate: undefined });
      history.push('/collection/edit/' + collection_id);
      setCollectionsModalOpenState({ key: 'collectionModal', state: false });
      getCollectionsIds({ withInitialCollectionsCount: false });
      fetchGalleriesTags();
    }

    setCollectionsModalOpenState({ key: 'collectionModal', state: false });
    this.setState({ loading: false });
  }

  toggle() {
    const { setCollectionsModalOpenState, collectionModal } = this.props;

    const newCollectionModalOpenState = !collectionModal;

    setCollectionsModalOpenState({ key: 'collectionModal', state: newCollectionModalOpenState });
    if (this.titleRef.current) {
      this.titleRef.current.focus();
    }

    if (newCollectionModalOpenState && this.props.availableRules.collectionsLeft > 0) {
      this.props.trackEventAction({
        name: 'modal-window-opened',
        payload: {
          modal_id: 'create-collection',
          url: window.location.href
        }
      });
    }
  }

  emailConfirmationToggle() {
    if (this.state.hideEmailConfirmationButton) {
      return;
    }

    const { setCollectionsModalOpenState, collectionModal } = this.props;
    setCollectionsModalOpenState({ key: 'collectionModal', state: !collectionModal });
  }

  async sendConfirmationEmail() {
    this.setState({ loading: true });
    const { sendConfirmationEmail } = this.props;
    const response = await sendConfirmationEmail();
    if (!response) {
      this.setState({
        emailConfirmationBody: getTranslationKey('generalError')
      });

      return;
    }

    if (response.error) {
      this.setState({
        emailConfirmationBody: getTranslationKey('generalError')
      });

      return;
    }

    this.setState({
      emailConfirmationBody: getTranslationKey('showEmailConfirmation.success'),
      loading: false,
      hideEmailConfirmationButton: true
    });
  }

  renderGalleryPresetSelection() {
    return (
      <div className="m-b-10">
        <div className="mb-1">
          <small className="pt-tt-preset-selection-gallery-creation">
            <b>{getTranslationKey('collection.selectGalleryPreset')}</b>
          </small>
        </div>
        <GalleryPresetSelect
          selectedPresetId={this.state.galleryPreset && this.state.galleryPreset._id}
          faceRecognition={this.state.faceRecognition}
          onFaceDataChange={(faceRecognition: FaceRecognition) => {
            this.setState({
              faceRecognition
            });
          }}
          onChange={(galleryPreset: GalleryPreset) => {
            try {
              const update: Partial<CreateCollectionState> = {
                galleryPreset,
                faceRecognition: { enabled: galleryPreset.faceRecognition?.enabled || false }
              };

              if (galleryPreset._userbrand) {
                update.userBrand = galleryPreset._userbrand;
              }
              if (galleryPreset._scrappbookGalleryTemplate) {
                // The new design lists Valencia in place of the old Spring Vibes variants.
                const presetTemplateId = this.props.isNewDesign
                  ? getReplacementTemplateId(galleryPreset._scrappbookGalleryTemplate)
                  : galleryPreset._scrappbookGalleryTemplate;
                const selectedTemplate = this.props.scrappbookTemplates.find(
                  (t) => t._id === presetTemplateId
                );
                if (selectedTemplate) {
                  update.selectedTemplateId = selectedTemplate._id;
                  update.selectedTemplate = selectedTemplate;
                }
              }

              this.setState(update as CreateCollectionState);
            } catch (e) {
              console.log(e);
            }
          }}
        />
      </div>
    );
  }

  renderBrandSelection() {
    return (
      <div>
        <div className="mb-1">
          <small className="pt-tt-brand-selection-gallery-creation">
            <b>
              {this.props.userType === 'SB'
                ? getTranslationKey('collection.selectUserBrandSB')
                : getTranslationKey('collection.selectUserBrand')}
            </b>
          </small>
        </div>
        <UserBrandSelection
          selectedUserBrand={this.state.userBrand}
          onChange={(userBrand: string) => this.setState({ userBrand })}
        />
      </div>
    );
  }

  renderModalBody() {
    const { subscription, scrappbookTemplates } = this.props;

    if (this.state.showEmailConfirmation) {
      return (
        <div>
          <div
            dangerouslySetInnerHTML={{
              __html: this.state.emailConfirmationBody
            }}
          />
          <div className="row">
            <div className="col-lg-12">
              {this.state.loading && (
                <div>
                  <Progress className="m-t-10 w-100-p" animated color="success" value="100">
                    {getTranslationKey('showEmailConfirmation.loading')}
                  </Progress>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div>
        {this.state.step === 1 ? (
          <div className="row">
            <div className="col-lg-12">
              <div className="mb-1">
                <small>
                  <b>{getTranslationKey('collection.title')}</b>
                </small>
              </div>
              <InputGroup className="m-b-10">
                <input
                  data-cy="modal-title"
                  className="form-control help_create_collection_title"
                  autoFocus
                  ref={this.titleRef}
                  onChange={({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
                    this.onChangeText(value, 'title');
                  }}
                  value={this.state.title}
                  placeholder={getTranslationKey('collection.title')}
                />
                <InputGroupText id="createGalleryTitleTooltip">
                  <i className="fa fa-icon fa-question-circle" />
                </InputGroupText>

                <UncontrolledTooltip placement="right" target="createGalleryTitleTooltip">
                  {getTranslationKey('collectionTitleTooltip')}
                </UncontrolledTooltip>
              </InputGroup>
              <div className="mb-1">
                <small>
                  <b>{getTranslationKey('collection.name')}</b>
                </small>
              </div>
              <InputGroup className="m-b-10">
                <Input
                  data-cy="modal-customer-name"
                  className="help_create_collection_intern"
                  onChange={({ target: { value } }: ChangeEvent<HTMLInputElement>) =>
                    this.onChangeText(value, 'name')
                  }
                  value={this.state.name}
                  placeholder={getTranslationKey('collection.name')}
                />
                <InputGroupText id="createGalleryNameTooltip">
                  <i className="fa fa-icon fa-question-circle" />
                </InputGroupText>

                <UncontrolledTooltip placement="right" target="createGalleryNameTooltip">
                  {getTranslationKey('interNameToolTip')}
                </UncontrolledTooltip>
              </InputGroup>

              <InputGroup className="m-b-10">
                <div className="mb-1">
                  <small>
                    <b>{getTranslationKey('collection.eventDate')}</b>
                  </small>
                </div>
                <DateTimePicker
                  className="w-100-p"
                  time={false}
                  value={this.state.eventDate ? new Date(this.state.eventDate as any) : null}
                  placeholder={getTranslationKey('eventDate')}
                  onChange={(eventDate: Date) => this.setState({ eventDate })}
                />
              </InputGroup>

              {this.props.userType !== 'SB' && this.renderBrandSelection()}
            </div>
          </div>
        ) : (
          <div className="row">
            <div className="col-lg-12">
              {!this.props.onboardingProcess && this.renderGalleryPresetSelection()}
              {!this.props.onboardingProcess && this.renderBrandSelection()}

              <div className="mb-1 m-t-10">
                <small>
                  <b>{getTranslationKey('collection.selectDesign')}</b>
                </small>
              </div>

              {scrappbookTemplates.length > 0 ? (
                <div className="row">
                  <div className="col-12 col-sm-6">
                    <Input
                      type="select"
                      data-cy="help_collection_template_create"
                      className="help_collection_template_create"
                      onChange={({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
                        const selectedTemplate = scrappbookTemplates.find((t) => t._id === value);
                        this.setState({
                          selectedTemplateId: value,
                          selectedTemplate: selectedTemplate || {}
                        });
                      }}
                      value={this.state.selectedTemplateId}
                    >
                      {scrappbookTemplates.map(
                        (template) =>
                          (!this.props.onboardingProcess ||
                            (this.props.onboardingProcess &&
                              template.userIsAllowedToUseTemplate)) && (
                            <option key={template._id} value={template._id}>
                              {getTemplateTitle(template, this.props.isNewDesign)}
                            </option>
                          )
                      )}
                    </Input>
                    <p className="m-t-15 h-150">
                      {getTemplateDescription(
                        getTranslationKey(
                          `sb_templates.descriptions.${this.state.selectedTemplateId}`
                        ),
                        this.state.selectedTemplate,
                        this.props.isNewDesign
                      )}
                    </p>
                    {!this.state.selectedTemplate.inDevelopment && !this.props.onboardingProcess && (
                      <a
                        className="btn btn-primary w-100-p"
                        target="_blank"
                        href={this.state.selectedTemplate.previewUrl}
                        rel="noopener noreferrer"
                      >
                        {getTranslationKey('template_preview')}
                      </a>
                    )}
                    {!this.state.selectedTemplate.userIsAllowedToUseTemplate &&
                      this.state.selectedTemplate.isPurchable && (
                        <div className="mt-3">
                          {this.state.selectedTemplate.presale ? (
                            <div>
                              {!subscription.purchased_templates.includes(
                                this.state.selectedTemplate.name || ''
                              ) ? (
                                <Alert color="warning">
                                  {getTranslationKey('sb_templates.templatePresale')}
                                </Alert>
                              ) : (
                                <Alert color="warning">
                                  {getTranslationKey(
                                    'sb_templates.templatePresaleAlreadyPurchased'
                                  )}
                                </Alert>
                              )}
                            </div>
                          ) : (
                            <Alert color="danger">
                              {getTranslationKey('templateMoreInformation')}
                            </Alert>
                          )}
                          <button
                            data-cy="PurchaseTemplate"
                            className="btn btn-primary w-100-p m-b-10"
                            onClick={() => {
                              this.props.setModalOpenState({
                                key: 'upgradeModal',
                                state: true,
                                modalData: {
                                  addon: 'template',
                                  templateId: this.state.selectedTemplate._id
                                }
                              });
                              this.toggle();
                              this.props.logUserActivity({
                                action_data: this.state.selectedTemplate.name,
                                action: 'GO_TO_TEMPLATE_LP',
                                logAsHubpostNote: true,
                                leadScore: 2
                              });
                            }}
                          >
                            {getTranslationKey('purchase_template')}
                          </button>
                        </div>
                      )}
                  </div>
                  <div className="col-12 col-sm-6">{this.renderTemplatePreview()}</div>
                </div>
              ) : (
                <p>loading</p>
              )}
            </div>
          </div>
        )}
        <div className="row">
          <div className="col-lg-12">
            {this.state.loading && (
              <div>
                <Progress className="m-t-10 w-100-p" animated color="success" value="100">
                  {getTranslationKey('creatingSite')}
                </Progress>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  renderModalFooter() {
    const isTitleFilled = this.state.title.trim().length > 0;

    if (this.state.showEmailConfirmation) {
      return (
        <div>
          {!this.state.hideEmailConfirmationButton && (
            <button
              className={classnames('btn btn-primary help_collection_template_create pull-right', {
                'w-100-p': this.props.onboardingProcess,
                'm-t-10': this.props.onboardingProcess
              })}
              onClick={this.sendConfirmationEmail}
              disabled={this.state.loading}
            >
              {getTranslationKey('showEmailConfirmation.button')}
            </button>
          )}
        </div>
      );
    }

    return this.state.isSmallScreen ? (
      <div className="d-block w-100-p">
        <button
          className={classnames(
            'btn btn-primary pt-create-gallery gtm-create-gallery pull-right w-100-p',
            {
              'm-t-10': this.props.onboardingProcess
            }
          )}
          onClick={this.createCollection}
          disabled={this.state.loading || !isTitleFilled}
        >
          {getTranslationKey('create')}
        </button>
      </div>
    ) : (
      <div>
        {!this.props.onboardingProcess && (
          <button
            className="btn btn-danger pull-left m-r-5"
            onClick={this.state.step === 1 ? this.toggle : this.setStep}
          >
            {this.state.step === 1 ? getTranslationKey('abort') : getTranslationKey('back')}
          </button>
        )}
        <button
          data-cy="modal-title-field"
          className={classnames('btn btn-primary pt-create-gallery pull-right', {
            'w-100-p': this.props.onboardingProcess,
            'm-t-10': this.props.onboardingProcess,
            'gtm-create-gallery': this.state.step === 2 && this.props.userType === 'SB'
          })}
          onClick={
            this.state.step === 1 && this.props.userType === 'SB'
              ? this.setStep
              : this.createCollection
          }
          disabled={this.state.loading || !isTitleFilled}
        >
          {this.state.step === 1 && this.props.userType === 'SB'
            ? getTranslationKey('nextStep')
            : getTranslationKey('create')}
        </button>
      </div>
    );
  }

  renderTemplatePreview() {
    return this.state.showPreviewVideo ? (
      <div className="template-preview">
        <div className="videowrap">
          <video playsInline autoPlay loop muted>
            <source type="video/mp4" src={this.state.selectedTemplate.assetUrl} />
          </video>

          <div className="videoframe">
            <img
              className="img-fluid"
              src="https://cf.scrappbook.de/wp-content/uploads/2019/09/Header-Smartphone-Mockup-1.png"
              alt=""
            />
          </div>
        </div>
      </div>
    ) : (
      <div className="template-preview">
        <div className="videowrap">
          <div className="videoframe">
            <img
              className="img-fluid"
              src="https://cf.scrappbook.de/wp-content/uploads/2019/09/Header-Smartphone-Mockup-1.png"
              alt=""
            />
          </div>
        </div>
      </div>
    );
  }

  renderModals() {
    const {
      setUpgradeAccountOpenState,
      availableRules = {} as AvailableRules,
      collectionModal
    } = this.props;
    const limitIsExceeded = collectionModal && availableRules.collectionsLeft === 0;

    if (limitIsExceeded) {
      setUpgradeAccountOpenState({
        key: 'upgradeModal',
        state: true,
        callback: this.toggle,
        desiredAction: 'collections'
      });

      return;
    }

    if (this.state.showEmailConfirmation) {
      if (!this.state.hideEmailConfirmationButton) {
        return (
          <Modal
            centered
            isOpen={collectionModal}
            wrapClassName="modal-dark"
            size="md"
            onKeyPress={this.handleCreateKeyPress}
            toggle={this.emailConfirmationToggle}
          >
            <ModalHeader toggle={this.emailConfirmationToggle}>
              {getTranslationKey('showEmailConfirmation.title')}
            </ModalHeader>
            <ModalBody>{this.renderModalBody()}</ModalBody>
            <ModalFooter>{this.renderModalFooter()}</ModalFooter>
          </Modal>
        );
      }

      return (
        <Modal
          centered
          isOpen={collectionModal}
          wrapClassName="modal-dark"
          size="md"
          onKeyPress={this.handleCreateKeyPress}
        >
          <ModalHeader>{getTranslationKey('showEmailConfirmation.title')}</ModalHeader>
          <ModalBody>{this.renderModalBody()}</ModalBody>
          <ModalFooter>{this.renderModalFooter()}</ModalFooter>
        </Modal>
      );
    }

    return (
      <Modal
        centered
        isOpen={collectionModal}
        toggle={this.toggle}
        wrapClassName="modal-dark"
        size="lg"
        onKeyPress={this.handleCreateKeyPress}
      >
        <ModalHeader toggle={this.toggle}>
          {this.props.userType === 'SB'
            ? getTranslationKey('collection.createScrAppBook')
            : getTranslationKey('collection.create')}
        </ModalHeader>
        <ModalBody>{this.renderModalBody()}</ModalBody>
        <ModalFooter>{this.renderModalFooter()}</ModalFooter>
      </Modal>
    );
  }

  render() {
    const { renderWithOutModal } = this.props;

    return (
      <div className="inline">
        {!renderWithOutModal && (
          <button
            id="create-collection-button"
            className="btn btn-success help_create_collection"
            onClick={this.toggle}
          >
            <i className="fa fa-icon fa-plus" />{' '}
            <span className="d-sm-down-none">
              {this.props.userType === 'SB'
                ? getTranslationKey('collection.createScrAppBook')
                : getTranslationKey('collection.create')}
            </span>
            <span className="d-md-none">
              {this.props.userType === 'SB'
                ? getTranslationKey('collection.scrappbook')
                : getTranslationKey('collection.collection')}
            </span>
          </button>
        )}

        {renderWithOutModal ? (
          <>
            {this.renderModalBody()}
            {this.renderModalFooter()}
          </>
        ) : (
          <>{this.renderModals()}</>
        )}
      </div>
    );
  }
}

interface AuthState {
  defaultUserBrand: any;
  type: string;
  additionalFeatures: any;
  subscription: Subscription;
  digistore: {
    discountKey?: {
      affiliate?: string;
    };
  };
  email: string;
  registrationProcess: RegistrationProcess;
  featureFlags?: { newDesign?: boolean };
}

interface CollectionsState {
  scrappbookTemplates: ScrappbookTemplate[];
  ids: string[];
}

interface AccessRulesState {
  availableRules: AvailableRules;
}

interface ModalsState {
  collectionModal: boolean;
}

interface RootState {
  auth: AuthState;
  collections: CollectionsState;
  accessRules: AccessRulesState;
  modals: ModalsState;
}

function mapStateToProps({
  auth: {
    defaultUserBrand,
    type,
    additionalFeatures,
    subscription,
    digistore,
    email,
    registrationProcess,
    featureFlags
  },
  collections: { scrappbookTemplates, ids },
  accessRules: { availableRules },
  modals: { collectionModal }
}: RootState) {
  return {
    defaultUserBrand,
    additionalFeatures,
    userType: type,
    subscription,
    digistore,
    availableRules,
    affiliate: digistore.discountKey ? digistore.discountKey.affiliate : null,
    userEmail: email,
    scrappbookTemplates,
    collectionModal,
    collectionsIds: ids,
    registrationProcess,
    isNewDesign: Boolean(featureFlags?.newDesign)
  };
}

export default connect(mapStateToProps, { ...actions, fetchGalleriesTags })(CreateCollection);
